import { useState, useRef, useEffect, useContext } from 'react';
import { MessageCombobox } from '@src/components/combobox/Combobox';
import { FiltersRenderer } from '@src/components/dynamicSelector/FiltersRenderer';
import { InputComponent } from '@src/components/input/Input';
import { FilteredIdsRenderer } from '@src/components/dynamicSelector/FilteredIdsRenderer';
import { IdsOptionRenderer } from '@src/components/dynamicSelector/IdsOptionRenderer';
import {
  FetchContext,
  type ArticleReference,
  type FetchDynamicItemsPayload,
} from '@src/scenario-editor/providers/fetch.context';
import { getReferenceLabel, getUnresolvedIds, sortOptionsByLabel } from '@src/components/dynamicSelector/articleReferences';
import { ArticleOptionRenderer } from '@src/components/dynamicSelector/ArticleOptionRenderer';
import { ArticlePreviewCard } from '@src/components/dynamicSelector/ArticlePreviewCard';
import { FitFactPreviewCard } from '@src/components/dynamicSelector/FitFactPreviewCard';
import { LoadingSkeleton } from '@src/components/dynamicSelector/LoadingSkeleton';
// import { adjustPopoverPosition } from '@src/components/dynamicSelector/utils.ts';
import { useSpring, animated } from '@react-spring/web';
import { Selectbox } from '@src/components/selectbox/Selectbox';
import { CustomTooltip } from '@src/components/tooltip/Tooltip.tsx';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { getCurrentInfoModalCounter } from '@src/atoms';
import { Icon } from '@src/components/icon/Icon';
import { Button } from '@/components/ui/button';
import { mergePickedIds, pickedLabels } from '@src/components/dynamicSelector/pickedItems';

interface SelectionItem {
  ids?: string[],
  type: 'ids' | 'filters',
  filters: {
    categories?: string[],
    tags?: string[],
    maxNumber?: number,
  }
}
interface MessageErrorItem {
  message: string;
}
interface ValidationErrors {
  message?: string;
  ids?: { message: string } | { message: string }[],
  filters?: {
    categories?: MessageErrorItem | MessageErrorItem[],
    tags?: MessageErrorItem | MessageErrorItem[],
    maxNumber?: MessageErrorItem | MessageErrorItem[];
  }
}

function getError (validationErrors: ValidationErrors, key: 'ids') {
  if ((validationErrors?.[key] as MessageErrorItem)?.message) {
    return (validationErrors?.[key] as MessageErrorItem)?.message;
  }

  if (Array.isArray(validationErrors?.[key])) {
    return (validationErrors?.[key] as MessageErrorItem[])?.map((error: { message: string }) => error?.message).join(', ');
  }

  return;
}

function getFilterError (validationErrors: ValidationErrors, key: 'categories' | 'tags' | 'maxNumber') {
  if ((validationErrors?.filters?.[key] as MessageErrorItem)?.message) {
    return (validationErrors?.filters?.[key] as MessageErrorItem)?.message;
  }

  if (Array.isArray(validationErrors?.filters?.[key])) {
    return (validationErrors?.filters?.[key] as MessageErrorItem[])?.map((error: { message: string }) => error?.message).join(', ');
  }

  return;
}

export interface DynamicSelectionProps {
  value?: SelectionItem,
  validationErrors?: ValidationErrors,
  onValueChange?: (value: SelectionItem, extraOpts?: Record<string, unknown>) => void,
  withDynamicMaxNumber?: boolean,
  fetchItemsMethod?: string,
  fetchFilteredItemsMethod?: string,
  // LF360-2023: explains selected ids the fetched list does not offer
  fetchReferencesMethod?: 'fetchArticleReferences',
  // LF360-2015: A-Z list that keeps selected rows marked, with a hover preview when the consumer can fetch one
  browsable?: boolean,
  // LF360-2030: the consumer's search for individually selected items, used instead of the list when given
  pickItemsMethod?: 'pickArticles' | 'pickFitFacts',
  itemName?: string;
  disabled?: boolean;
}

interface Option {
  label: string,
  value: string,
}

type SelectedMode = 'filters' | 'ids';

export function DynamicSelection ({
  value,
  validationErrors,
  onValueChange,
  withDynamicMaxNumber,
  fetchItemsMethod,
  fetchFilteredItemsMethod,
  fetchReferencesMethod,
  browsable = false,
  pickItemsMethod,
  itemName = 'Item',
  disabled = false,
}: DynamicSelectionProps) {
  const selectedType = value?.type ?? 'filters';
  const [selectedMode, setSelectedMode] = useState<SelectedMode>(selectedType);
  const [isIdsSelecterOpened, setIsIdsSelecterOpened] = useState(false);
  const [isItemsFetching, setIsItemsFetching] = useState<boolean | null>(null);
  const [isFilterFetching, setIsFilterFetching] = useState<boolean | null>(null);
  const [fetchedData, setFetchedData] = useState<FetchDynamicItemsPayload['data']>({
    ids: [],
    categories: [],
    tags: [],
  });
  const [selectedIdsValue, setSelectedIdsValue] = useState<Option[]>([]);
  const [categoriesValue, setCategoriesValue] = useState<Option[]>([]);
  const [tagsValue, setTagsValue] = useState<Option[]>([]);
  const [fetchedFilteredItems, setFetchedFilteredItems] = useState<string[]>();
  const [references, setReferences] = useState<Record<string, ArticleReference>>({});
  // LF360-2030: names of items added through the consumer's search, which the
  // cached list fetched on open may not hold
  const [pickedNames, setPickedNames] = useState<Record<string, string>>({});
  const fetchContext = useContext(FetchContext)!;
  const pickItems = pickItemsMethod ? fetchContext?.[pickItemsMethod] : undefined;
  const shouldShowErrorWarnings = (getCurrentInfoModalCounter() ?? 0) > 1;

  const [{ opacity }, api] = useSpring(() => ({
    opacity: 0,
    config: { tension: 1000, friction: 30 },
  }));

  useEffect(() => {
    if (isItemsFetching !== null && !isItemsFetching) {
      api.set({ opacity: 0 });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      api.start({
        opacity: 1,
      });
    }
  }, [isItemsFetching, selectedMode, api]);

  if (!fetchContext) {
    throw new Error('FetchContext is not provided');
  }

  const filteredIdsContentRef = useRef<HTMLDivElement>(null);
  const listedOptions = fetchedData.ids
    .map((option) => ({ label: option.text ?? '', value: option.id ?? '' }))
  const fetchedOptions = [
    ...listedOptions,
    ...Object.entries(pickedNames)
      .filter(([id]) => !listedOptions.some((option) => option.value === id))
      .map(([id, label]) => ({ label, value: id })),
  ]
  const fitFactOptions = fetchedData.ids
    .filter((option) => !(value!.ids ?? []).includes(option.id))
    .map((option) => ({ label: option.text ?? '', value: option.id ?? '' }))
  const pickerOptions = browsable ? sortOptionsByLabel(fetchedOptions) : fitFactOptions;
  const renderPreview = (optionValue: string) => {
    const item = fetchedData.ids.find((option) => option.id === optionValue);
    if (!item) return null;
    if (!browsable) return <FitFactPreviewCard text={item.text ?? ''} source={item.itemUrl}/>;
    return fetchContext.fetchArticlePreview ? <ArticlePreviewCard id={item.id} title={item.text ?? ''}/> : null;
  };
  const categoryOptions = fetchedData.categories.map((option) => ({ label: option.name ?? '', value: option.id ?? '' }));
  const tagOptions = fetchedData.tags.map((option) => ({ label: option.name ?? '', value: option.id ?? '' }));

  useEffect(() => {
    setIsItemsFetching(true);
    fetchContext[fetchItemsMethod as 'fetchFitFacts' | 'fetchArticles']?.()
      .then((fetchedData) => {
        setFetchedData(fetchedData.data);
        setIsItemsFetching(false);
      })
      .catch((error) => {
        setIsItemsFetching(false);
        console.error('error', error);
      });
  }, []);

  const fetchReferences = fetchReferencesMethod ? fetchContext[fetchReferencesMethod] : undefined;
  const unresolvedIds = isItemsFetching === false
    ? getUnresolvedIds(value!.ids ?? [], fetchedOptions.map((option) => option.value))
    : [];
  const unresolvedIdsKey = unresolvedIds.join(',');

  useEffect(() => {
    const unknownIds = unresolvedIds.filter((id) => !references[id]);
    if (!fetchReferences || !unknownIds.length) {
      return;
    }

    let isCancelled = false;
    fetchReferences(unknownIds)
      .then(({ data }) => {
        if (isCancelled) return;
        setReferences((prev) => ({ ...prev, ...Object.fromEntries(data.map((reference) => [reference.id, reference])) }));
      })
      .catch((error) => {
        console.error('Error fetching item references', error);
      });

    return () => {
      isCancelled = true;
    };
  }, [unresolvedIdsKey]);

  useEffect(() => {
    setSelectedIdsValue(
      (value!.ids ?? []).map((item) => {
        const label = fetchedData.ids.find(option => option.id === item)?.text
          ?? pickedNames[item]
          ?? (fetchReferences ? getReferenceLabel(references[item], itemName) : item);
        return { label, value: item };
      })
    );
  }, [fetchedData.ids, value!.ids, references, pickedNames]);

  const addThroughSearch = () => {
    pickItems?.(value!.ids ?? [])
      .then((picked) => {
        if (!picked?.length) return;
        setPickedNames((prev) => ({ ...prev, ...pickedLabels(picked) }));
        onValueChange?.({ ...value!, type: 'ids', ids: mergePickedIds(value!.ids ?? [], picked) });
      })
      .catch((error) => {
        console.error('Error picking items', error);
      });
  };

  useEffect(() => {
    setCategoriesValue(
      (value!.filters.categories ?? []).map((item) => {
        const option = fetchedData.categories.find(option => option.id === item);
        return ({ label: option?.name ?? item, value: option?.id ?? item })
      })
    );
  }, [fetchedData.categories, value!.filters.categories]);

  useEffect(() => {
    setTagsValue(
      (value!.filters.tags ?? []).map((item) => {
        const option = fetchedData.tags.find(option => option.id === item);
        return ({ label: option?.name ?? item, value: option?.id ?? item })
      })
    )
  }, [fetchedData.tags, value!.filters.tags]);

  useEffect(() => {
    // INFO: this is the only way to move popover content to adjust it according to the parent popup
    setTimeout(() => {
      const { current } = filteredIdsContentRef;
      if (isIdsSelecterOpened) {
        const parentWrapper = current?.parentElement;
        if (parentWrapper) {
          // INFO: leave this for now here as a reference on how to adjust the popover position
          // adjustPopoverPosition(parentWrapper);
        }
      }
    }, 0);
  }, [isIdsSelecterOpened]);

  useEffect(() => {
    if (isItemsFetching === null || isItemsFetching) {
      return;
    }

    setIsFilterFetching(true);
    fetchContext[fetchFilteredItemsMethod as 'fetchFilteredFitFacts' | 'fetchFilteredArticles']?.(
      tagsValue.map(item => item.value),
      categoriesValue.map(item => item.value)
    )
      .then((result) => {
        setFetchedFilteredItems(result.data.ids.map(fact => fact.id));
        setIsFilterFetching(false);
      })
      .catch((error) => {
        console.error('error', error);
      });
  }, [categoriesValue, tagsValue, isItemsFetching])

const genericError = validationErrors?.message as never;
  const idsErrors = getError(validationErrors!, 'ids');
  const categoriesErrors = getFilterError(validationErrors!, 'categories');
  const tagsErrors = getFilterError(validationErrors!, 'tags');
  const maxNumberErrors = getFilterError(validationErrors!, 'maxNumber');

  return (
    <div className="fixed w-[450px] min-h-[calc(100%-57px)] left-0 top-[57px] bg-white shadow-lg rounded-md">
      <div
        className="grid grid-cols-[150px_minmax(200px,_1fr)] text-sm pt-5 py-2 px-4 max-h-[70vh] overflow-y-auto overflow-x-hidden"
      >
        <div className="py-2 pt-4 text-label-foreground">Type</div>
        <div className="py-2 pl-2">
          <Selectbox
            disabled={disabled}
            options={[
              { label: 'Dynamically selected', value: 'filters' },
              { label: 'Individually selected', value: 'ids' },
            ]}
            value={selectedMode}
            onValueChange={(mode: string) => {
              setSelectedMode(mode as SelectedMode);
              onValueChange?.({ ...value!, type: mode as SelectedMode });
            }}
          />
        </div>
      </div>
      {isItemsFetching !== null && isItemsFetching && <LoadingSkeleton/>}
      {isItemsFetching !== null && !isItemsFetching && (
        <animated.div
          style={{
            opacity: opacity,
          }}
        >
          {selectedMode === 'filters' && (
            <div>
              <div
                className="grid grid-cols-[150px_minmax(200px,_1fr)] text-sm py-2 px-4 max-h-[70vh] overflow-y-auto overflow-x-hidden"
              >
                <div className="flex justify-between items-center py-2 pt-5 text-label-foreground">
                  Category
                  {!!categoriesErrors && shouldShowErrorWarnings && (
                    <div className="right-0 top-1">
                      <CustomTooltip content={categoriesErrors}>
                        <ExclamationTriangleIcon className="text-red-500"/>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
                <div className="pl-2 py-2 pt-4">
                  <MessageCombobox
                    hideSelectedOptions
                    filterPlaceholder="Search categories..."
                    value={categoriesValue}
                    options={categoryOptions}
                    onValueChange={(categories) => {
                      onValueChange?.({
                        ...value,
                        type: 'filters',
                        filters: {...value!.filters, categories: categories.map(item => item.value)}
                      });
                    }}
                    addBtnLabel="Add"
                    withCustomOptions={false}
                    Renderer={FiltersRenderer}
                    disabled={disabled}
                  />
                </div>
                <div className="flex justify-between items-center py-2 pt-5 text-label-foreground">
                  Tags
                  {!!tagsErrors && shouldShowErrorWarnings && (
                    <div className="right-0 top-1">
                      <CustomTooltip content={tagsErrors}>
                        <ExclamationTriangleIcon className="text-red-500"/>
                      </CustomTooltip>
                    </div>
                  )}
                </div>
                <div className="pl-2 py-2 pt-4">
                  <MessageCombobox
                    hideSelectedOptions
                    filterPlaceholder="Search tags..."
                    value={tagsValue}
                    options={tagOptions}
                    onValueChange={(tags) => {
                      onValueChange?.({
                        ...value,
                        type: 'filters',
                        filters: {...value!.filters, tags: tags.map(item => item.value)}
                      });
                    }}
                    addBtnLabel="Add"
                    withCustomOptions={false}
                    Renderer={FiltersRenderer}
                    disabled={disabled}
                  />
                </div>
                {withDynamicMaxNumber && (
                  <>
                    <div className="relative">
                      {!!maxNumberErrors && shouldShowErrorWarnings && (
                        <div className="absolute top-6 right-0">
                          <CustomTooltip content={maxNumberErrors}>
                            <ExclamationTriangleIcon className="text-red-500"/>
                          </CustomTooltip>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2 pt-5 text-label-foreground">
                        Max number
                      </div>
                    </div>
                    <div className="pl-2 py-2 pt-4">
                      <InputComponent
                        value={value?.filters?.maxNumber?.toString() ?? String(5)}
                        onValueChange={(number: string) => {
                          onValueChange?.({
                            ...value,
                            type: 'filters',
                            filters: {...value!.filters, maxNumber: Number(number) ?? 5}
                          });
                        }}
                        disabled={disabled}
                      />
                    </div>
                  </>
                )}
              </div>

              {!isFilterFetching && (
                <div className="border-t border-input p-4 text-label-foreground">
                  {(fetchedFilteredItems ?? []).length} {itemName}s found. {' '}
                  {/*INFO: unlock once ready*/}
                  {/*<a href="test" className="text-blue-600">Preview</a>*/}
                </div>
              )}
            </div>
          )}
          {selectedMode === 'ids' && (
            <div className="relative border-t mt-2 border-input">
              {(!!idsErrors || genericError) && shouldShowErrorWarnings && (
                <div className="absolute left-16 -top-12">
                  <CustomTooltip content={genericError ?? idsErrors}>
                    <ExclamationTriangleIcon className="text-red-500"/>
                  </CustomTooltip>
                </div>
              )}
              {pickItems ? (
                <FilteredIdsRenderer
                  disabled={disabled}
                  value={selectedIdsValue}
                  onValueChange={(ids = []) => {
                    onValueChange?.({...value!, ids: ids.map(item => item.value)});
                  }}
                  options={fetchedOptions}
                  references={references}
                >
                  {!disabled && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-between px-2"
                      onClick={addThroughSearch}
                    >
                      <Icon type="plus" />
                      {`Add ${itemName}`}
                    </Button>
                  )}
                </FilteredIdsRenderer>
              ) : (
              <MessageCombobox
                ref={filteredIdsContentRef}
                disabled={disabled}
                hideSelectedOptions
                shouldCloseOnSelect
                value={selectedIdsValue}
                options={pickerOptions}
                onOpenChange={setIsIdsSelecterOpened}
                onValueChange={(ids = []) => {
                  onValueChange?.({...value!, ids: ids.map(item => item.value)});
                }}
                addBtnLabel={`Add ${itemName}`}
                filterPlaceholder={`Search ${itemName}`}
                withCustomOptions={false}
                popoverContentOverrides={{
                  style: {width: '430px'},
                  align: 'start',
                  side: 'bottom'
                }}
                Renderer={({ value, onValueChange, children }) => (
                  <FilteredIdsRenderer
                    disabled={disabled}
                    value={value}
                    onValueChange={onValueChange}
                    options={fetchedOptions}
                    references={references}
                  >
                    {children}
                  </FilteredIdsRenderer>
                )}
                OptionRenderer={browsable ? ArticleOptionRenderer : IdsOptionRenderer}
                renderPreview={renderPreview}
              />
              )}
            </div>
          )}
        </animated.div>
      )}
    </div>
  )
}
